import React from 'react';
import { Box, HorizontalScroll, Typography } from '@drone-js/core';
import { ActivitiesPOIContainerProps } from 'component/page/search/City/ActivitiesPOIContainer/ActivitiesPOIContainer.types';
import { mapStateToProps, poiMapper } from 'component/page/search/City/ActivitiesPOIContainer/ActivitiesPOIContainer.selectors';
import { connect, useSelector } from 'react-redux';
import ContentContainer from 'component/common/ContentContainer/ContentContainer';
import { StringFormatter } from '@agoda/react-handyman';
import ActivityPOICard from 'component/common/ActivityPOICard/ActivityPOICard';
import queryActivitiesGqlApi from 'store/api/activitiesGqlApi';
import { ActivityGqlOperation, OperationKey, SearchRequestType } from 'store/api/api.types';
import { PointOfInterest } from 'gql/autogen/PointOfInterest';
import { ActivityPOI } from 'component/common/ActivityPOICard/ActivityPOICard.types';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesPoiContainerSeenEventActivitiesFieldsV1 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesPoiContainerSeenEventActivitiesManifestV1 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { InView } from 'react-intersection-observer';
import { selectScreenSize } from 'store/slice/screen';
import Feature from 'component/common/Experiment/Feature';
import { FeatureName } from 'core/constant/FeatureName';
import { SearchType } from 'gql/autogen/SearchType';
import { useExperimentIsB } from 'component/common/Experiment/Experiment';
import { ExperimentId } from 'core/type/ExperimentId';
import { SortCode } from 'gql/autogen/SortCode';
import { SortOrder } from 'gql/autogen/SortOrder';
import { selectFeatureBehaviorFromBootstrap } from 'store/slice/bootstrap';

const ActivitiesPOIContainerComponent: React.FC<ActivitiesPOIContainerProps> = (props) => {
    const { config } = props;
    const { cms, geo } = config;
    const [isLoading, setIsLoading] = React.useState(true);
    const initialPoiData = [{}, {}, {}, {}, {}] as ActivityPOI[];
    const [poiList, setPoiLIst] = React.useState<ActivityPOI[]>(initialPoiData);
    const screenSize = useSelector(selectScreenSize);
    const isHorizontalScrollPOIExperiment = useExperimentIsB(ExperimentId.ACT_4712);
    const isSearchFlowRefactor = useSelector(selectFeatureBehaviorFromBootstrap).SearchFlowRefactor;

    React.useEffect(() => {
        setIsLoading(true);
        queryActivitiesGqlApi({
            args: {
                props: {
                    searchType: isSearchFlowRefactor ? SearchType.POINT_OF_INTEREST_BY_CITY : SearchType.CITY,
                    searchRequestType: SearchRequestType.POINT_OF_INTEREST,
                    pagination: { number: 1, size: 50 },
                    defaultSort: isSearchFlowRefactor ? {} : { code: SortCode.ActivitiesPoiCarousel, order: SortOrder.Descending },
                },
                customStateHandling: true,
            },
            operation: ActivityGqlOperation.search,
            key: OperationKey.ActivitiesPOI,
            onSuccess: (_operation, _args, response) => {
                setIsLoading(false);
                const poiResponse = (response?.result?.pointOfInterests as PointOfInterest[]) || [];
                const poiData = poiMapper(poiResponse, config);
                setPoiLIst(poiData);
            },
            onError: () => {
                setIsLoading(false);
            },
        });
    }, [config, isSearchFlowRefactor]);

    const onSeen = (inView: boolean) => {
        if (!isLoading && poiList.length > 0 && inView) {
            trackElementEvent<ActivitiesPoiContainerSeenEventActivitiesFieldsV1>(ActivitiesPoiContainerSeenEventActivitiesManifestV1.ids, {});
        }
    };

    if (!geo || geo.cityId <= 0) {
        return null;
    }

    const header = StringFormatter.formatToJsx(cms[327762], geo?.cityName);

    if (!poiList.length && !isLoading) {
        return null;
    }

    const content = (
        <>
            <Typography variant="heading-l" mb="12">
                {header}
            </Typography>
            <InView onChange={onSeen} triggerOnce={true} threshold={0.75}>
                <HorizontalScroll display="flex" data-testid="activities-poi-list">
                    {isHorizontalScrollPOIExperiment ? (
                        poiList.map((poi, index) => (
                            // Do not remove the Box element otherwise card content will not be displayed properly
                            <Box key={index} display="flex">
                                <ActivityPOICard index={index} poi={poi} isLoading={isLoading} />
                            </Box>
                        ))
                    ) : (
                        <Box display="flex">
                            {poiList.map((poi, index) => (
                                // Do not remove the Box element otherwise card content will not be displayed properly
                                <Box key={index} display="flex">
                                    <ActivityPOICard index={index} poi={poi} isLoading={isLoading} />
                                </Box>
                            ))}
                        </Box>
                    )}
                </HorizontalScroll>
            </InView>
        </>
    );

    return (
        <Box
            backgroundColor="base.surface"
            minHeight={props.isPageContainerSpacing ? undefined : '415px'}
            pb={props.isPageContainerSpacing ? (screenSize.desktop ? '48' : '24') : '12'}
            pt={props.isPageContainerSpacing ? undefined : '24'}
            data-element-name="activities-poi-container"
            data-testid="activities-poi-container"
        >
            <Feature.Off name={FeatureName.PageContainerSpacing}>
                <ContentContainer display="flex" flexDirection="column" mx="auto" my="auto" enforceDefaultSpacing={true} maxWidth="100%">
                    {content}
                </ContentContainer>
            </Feature.Off>
            <Feature.On name={FeatureName.PageContainerSpacing}>{content}</Feature.On>
        </Box>
    );
};

ActivitiesPOIContainerComponent.displayName = 'ActivitiesPOIContainer';
export default connect(mapStateToProps)(ActivitiesPOIContainerComponent);
